import { Component, forwardRef, Inject, OnInit, ViewChild } from '@angular/core';
import * as _ from 'lodash';

import { provideParent, StateMap, UnitOfWork } from '../../services/common';
import { EditPropParent } from '../../controls/edit-prop.component';
import { EditManager } from '../../utils/common';
import { TypeCremeVersion } from '../../entities/EntityModels';

// import { TypeCremeVersionEditorComponent } from './type-creme-version-editor.component';
import { ConfirmFinishOverrideComponent } from '../staff/confirm-finish-override.component';
import { SelectableEntity } from '../../entities/projections/SelectableEntity';

export class CremeVersionEditData {
    inSubEditor: boolean;
}

@Component({
    selector: 'type-creme-versions',

    templateUrl: './type-creme-versions.html',
    providers: [provideParent(TypeCremeVersionsComponent, EditPropParent)]
})
export class TypeCremeVersionsComponent implements OnInit, EditPropParent {
    @ViewChild(ConfirmFinishOverrideComponent, { static: true }) _confirmFinishOverrideComponent: ConfirmFinishOverrideComponent;

    public _typeCremeVersionsEx: SelectableEntity<TypeCremeVersion>[] = [];

    _edm: EditManager<TypeCremeVersion>;
    _adding = false;

    private _isLoading = false;
    private _selectedTypeCremeVersion: TypeCremeVersion;

    _validationMessage: string;

    constructor(private _uow: UnitOfWork, private _stateMap: StateMap) {

    }

    ngOnInit() {
        this._edm = new EditManager<TypeCremeVersion>(this, this._uow, this._stateMap, 'Type Creme Versions');

        this.loadData();
    }

    loadData() {
        this._isLoading = true;

        this._uow.typeCremeVersionRepository.all().then(c => {

            if (c != null) {
                this._edm.entities = c.filter(t => t.cremeVersion.toLowerCase() != 'none');

                this.formatSelectableEntities();

                this._isLoading = false;
            }
        });
    }

    formatSelectableEntities() {
        this._typeCremeVersionsEx = [];
        if (this._edm.entities == null || this._edm.entities.length < 1) {
            return;
        }

        _.clone(this._edm.entities).forEach(element => {
            this._typeCremeVersionsEx.push(new SelectableEntity<TypeCremeVersion>(element, '#FFFFFF'));
        });

        this._typeCremeVersionsEx = _.orderBy(this._typeCremeVersionsEx, v => v.data.cremeVersion, 'desc');
    }

    checkIfNull(value: string) {
        if (value == '') {
            return null;
        } else {
            return value;
        }
    }

    getError(propName: string) {
        if (this._selectedTypeCremeVersion == null) {
            return;
        }

        const err = this._selectedTypeCremeVersion.getErrorFor(propName);
        if (err) {
            return err;
        }
        return null;
    }

    // EditManager overrides
    canApply() {
        if (this._edm.editing && this._uow.hasChanges()) {
            if (this._edm.currentEntity != null) {
                return (!this._edm.currentEntity.entityAspect.hasValidationErrors);
            }
            return true;
        }
        return false;
    }

    canCancel() {
        return (this._uow.hasChanges());
    }

    canDeactivate() {
        return !this._edm.hasEdits();
    }

    canSave() {
        if (!this._edm.hasEdits()) {
            return false;
        }
        if (!this._edm.validateBeforeSave()) {
            return false;
        }
        return true;
    }

    canShowBack() {
        if (!this._edm.editing) {
            return false;
        }
        return (!this._uow.hasChanges());
    }

    onAdd() {
        this._adding = true;

        const cv = this._uow.typeCremeVersionFactory.create();
        this._edm.entities.push(cv);
        this._edm.onSelect(cv);
    }

    onApply() {
        this._validationMessage = '';

        if (this._edm.editing) {
            if (this.isDuplicateTypeCremeVersionEntry(this._edm.currentEntity.cremeVersion)) {
                this._validationMessage = 'A Creme version with the same label already exists.';
                return;
            }

            this._edm.onApplyCore();
            this._adding = false;

            this.formatSelectableEntities();
        }

        return true;
    }

    onBack() {
        this._adding = false;
        this._edm.editing = false;
    }

    onCancel() {
        this._validationMessage = '';

        this._adding = false;

        this._edm.onCancelCore();
        this._edm.entities = null;

        this.loadData();
    }

    onEdit(px: SelectableEntity<TypeCremeVersion>) {

        if (px == null) {
            return;
        }

        this._selectedTypeCremeVersion = this.filterToTypeCremeVersionById(px.data.typeCremeVersionId);

        if (this._selectedTypeCremeVersion == null) {
            this._validationMessage = 'An error occurred trying to edit the selected Creme version.';
            return;
        }

        this._edm.onSelect(this._selectedTypeCremeVersion);
    }

    onRemove(cx: SelectableEntity<TypeCremeVersion>) {

        if (cx == null) {
            return;
        }

        this._selectedTypeCremeVersion = this.filterToTypeCremeVersionById(cx.data.typeCremeVersionId);

        if (this._selectedTypeCremeVersion == null) {
            this._validationMessage = 'An error occurred, could not remove the selected Creme version.';
            return;
        }

        const messages: string[] = []; // Only needed to satisfy a parameter
        this._confirmFinishOverrideComponent.showModal('Confirm Deletion of the selected Creme Version',
            'There may be Exposure Survey data assigned to this Creme version that will prevent its removal.', messages).then(ok => {
            if (ok) {
                if (this._selectedTypeCremeVersion.entityAspect.entityState.isAdded()) {
                    this._selectedTypeCremeVersion.entityAspect.rejectChanges();
                } else {
                    this._selectedTypeCremeVersion.entityAspect.setDeleted();
                }

                _.remove(this._edm.entities, this._selectedTypeCremeVersion);

                this.loadData();

                this.formatSelectableEntities();
            }
        });
    }

    onSave() {
        if (this._edm.hasEdits()) {
            this._edm.onSaveCore().then(() => {

                this._adding = false;
                this._edm.editing = false;

                this.loadData();
            });
        }
    }

    filterToTypeCremeVersionById(typeCremeVersionId: number): TypeCremeVersion {
        if (this._edm.entities == null || this._edm.entities.length < 1) {
            return null;
        }

        const versions = this._edm.entities.filter(e => e.typeCremeVersionId == typeCremeVersionId);
        if (versions != null && versions.length > 0) {
            return versions[0];
        }
    }

    isDuplicateTypeCremeVersionEntry(version: string): boolean {
        if (this._edm.entities == null) {
            return false;
        }

        const cv = this._edm.entities.filter(e => e.cremeVersion == version);
        if (cv != null && cv.length > 1) {
            return true;
        }

        return false;
    }
}
